import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthService } from '../services/authService';
import { User, UserRole } from '../types/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email?: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasRole: (roles: UserRole[]) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const authService = getAuthService();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔍 Initializing authentication...');
        const authenticated = await authService.isAuthenticated();
        console.log('✅ Authentication check result:', authenticated);
        setIsAuthenticated(authenticated);

        if (authenticated) {
          console.log('🔍 Getting current user...');
          const currentUser = await authService.getCurrentUser();
          console.log('✅ Current user:', currentUser);
          setUser(currentUser);
        } else {
          console.log('❌ User not authenticated');
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
        console.log('🏁 Auth initialization complete');
      }
    };

    initializeAuth();
  }, [authService]);

  const login = async (email?: string) => {
    try {
      console.log('🔍 Starting login process...');
      setLoading(true);
      await authService.login(email);
      console.log('✅ Login successful, getting current user...');
      const currentUser = await authService.getCurrentUser();
      console.log('✅ Current user after login:', currentUser);
      setUser(currentUser);
      setIsAuthenticated(true);
      console.log('🎉 Login process complete');
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const hasRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    hasRole,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 